import React from 'react';
import { Check, ArrowRight, Star, Target, Clock, TrendingUp, Sparkles, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const EliteGrowthMastery = () => {
  return (
    <div className="bg-exiser-light min-h-screen">
      {/* Hero */}
      <div className="bg-exiser-primary text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <Link to="/categories" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 uppercase tracking-wider text-xs font-bold transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Programmes
          </Link>
          <div className="inline-flex items-center gap-2 bg-exiser-accent/20 border border-exiser-accent/60 px-3 py-1 rounded-full mb-4">
            <Star className="w-4 h-4 text-exiser-accent fill-current" />
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-exiser-accent">
              Advisor Category • 3 Months • Advanced Programme
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-4">
            Elite Growth Mastery
          </h1>
          <p className="text-sm md:text-base text-purple-100 max-w-3xl font-sans">
            A 3-month advanced growth system for Life Insurance Advisors who are serious about
            scaling income, upgrading identity, and building long-term high-performance habits.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.2em]">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Duration: 3 Months</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              <span>For: Growth-Focused Advisors</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <span>Fees: ₹24,999</span>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/categories#elite-growth-mastery"
              className="inline-flex items-center gap-2 bg-exiser-accent text-exiser-primary px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] rounded-lg hover:bg-white shadow-lg"
            >
              Enroll From Programmes Page
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 border border-white/60 text-white px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] rounded-lg hover:bg-white hover:text-exiser-primary"
            >
              Book Clarity Session
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-12 md:py-16 space-y-10">
        {/* Who it's for */}
        <section className="bg-white rounded-2xl shadow-md border border-purple-100 p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold uppercase text-exiser-primary mb-4">
            Who Is This Programme For?
          </h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700 font-sans">
            <ul className="space-y-2">
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
                Life Insurance Advisors who want to move beyond basic selling
              </li>
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
                Advisors stuck at a certain income level for years
              </li>
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
                Professionals preparing for MDRT / COT / TOT journey
              </li>
            </ul>
            <ul className="space-y-2">
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
                Advisors who want strong systems, not random motivation
              </li>
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
                Those who completed basic level programmes and want the next level
              </li>
            </ul>
          </div>
        </section>

        {/* Pillars / Outcomes */}
        <section className="grid md:grid-cols-2 gap-6">
          <OutcomeCard
            title="1. Identity & Psychology Upgrade"
            items={[
              'Advanced growth mindset and identity engineering',
              'Breaking income and performance ceilings',
              'Daily mental conditioning and focus tools',
            ]}
          />
          <OutcomeCard
            title="2. High-Income Sales Mastery"
            items={[
              'Structuring powerful need-based presentations',
              'High-ticket closing and premium policy positioning',
              'Advanced objection handling and trust-building',
            ]}
          />
          <OutcomeCard
            title="3. Client Pipeline & Business Systems"
            items={[
              'Weekly action system for prospecting and follow-ups',
              'Client relationship and renewal growth framework',
              'Tracking dashboards for activities and income',
            ]}
          />
          <OutcomeCard
            title="4. Digital Presence & Future Roadmap"
            items={[
              'Simple digital visibility plan for advisors',
              'Content ideas to stay in front of clients',
              '90-day strategic roadmap beyond the programme',
            ]}
          />
        </section>

        {/* Experience */}
        <section className="bg-white rounded-2xl shadow-md border border-purple-100 p-6 md:p-8">
          <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-exiser-primary" />
            Your 3-Month Experience
          </h3>
          <ul className="space-y-2 text-sm text-gray-700 font-sans">
            <li className="flex gap-2">
              <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
              Weekly live sessions with implementation-focused coaching
            </li>
            <li className="flex gap-2">
              <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
              Action assignments to apply strategies in your market
            </li>
            <li className="flex gap-2">
              <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
              Tools, scripts and frameworks to reuse in your practice
            </li>
            <li className="flex gap-2">
              <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
              Clear targets and growth score tracking over 90 days
            </li>
          </ul>
        </section>

        {/* CTA */}
        <section className="bg-exiser-primary rounded-2xl p-6 md:p-8 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-xl md:text-2xl font-bold uppercase mb-2">
              Ready to Enter the Elite Growth Zone?
            </h3>
            <p className="text-sm text-purple-100 max-w-xl font-sans">
              Elite Growth Mastery is designed for advisors who are ready to treat their work like
              a real high-income profession and commit to transformation.
            </p>
          </div>
          <Link
            to="/categories#elite-growth-mastery"
            className="inline-flex items-center gap-2 bg-exiser-accent text-exiser-primary px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] rounded-lg hover:bg-white shadow-lg"
          >
            Enroll From Programmes Page
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </div>
    </div>
  );
};

const OutcomeCard = ({ title, items }) => (
  <div className="bg-white rounded-2xl border border-purple-100 p-6 shadow-sm">
    <h3 className="text-lg font-bold mb-3">{title}</h3>
    <ul className="space-y-2 text-sm text-gray-700 font-sans">
      {items.map((item, idx) => (
        <li key={idx} className="flex gap-2">
          <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export default EliteGrowthMastery;